import { FC } from 'react';

import { buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityProductDemoBlock } from '@lib/sanity/types';

const SCREENSHOT_WIDTH = 360;

type ProductDemoProps = {
  productDemo: SanityProductDemoBlock;
};

const ProductDemo: FC<ProductDemoProps> = ({ productDemo }) => {
  const { heading, items } = productDemo;

  if (!items || items.length === 0) return null;

  return (
    <section className="bg-landing-surface px-6 py-16">
      <div className="mx-auto w-full max-w-md md:max-w-5xl">
        {heading && (
          <h2 className="cms-heading mb-10 text-landing-ink">{heading}</h2>
        )}

        <div className="flex flex-col gap-12 md:grid md:grid-cols-3 md:gap-8">
          {items.map((item) => {
            const screenshotUrl = item.screenshot
              ? buildSanityImageUrl(item.screenshot)
                  .width(SCREENSHOT_WIDTH)
                  .quality(80)
                  .url()
              : null;

            return (
              <div key={item._key} className="text-center">
                {/* Phone bezel around the feature screenshot, centred above the copy. */}
                <div className="mx-auto mb-5 max-w-[180px] rounded-[1.75rem] bg-landing-ink p-2 shadow-[0_18px_44px_-20px_rgba(11,27,43,0.6)]">
                  <div
                    className="relative overflow-hidden rounded-2xl bg-landing-ink"
                    style={{ aspectRatio: '9 / 16' }}
                  >
                    {screenshotUrl && (
                      <img
                        src={screenshotUrl}
                        alt={item.benefit ?? item.featureName}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    )}
                  </div>
                </div>

                {item.benefit && (
                  <p className="cms-eyebrow mb-1.5 text-[color:var(--accent)]">
                    Powered by {item.featureName}
                  </p>
                )}
                {/* text-center: the global h3 style (styles/globals.css) sets
                    text-align: left, beating the centred card wrapper. */}
                <h3 className="mb-2 text-center text-lg font-bold leading-snug text-landing-ink">
                  {item.benefit ?? item.featureName}
                </h3>
                {item.description && (
                  <p className="text-sm leading-relaxed text-landing-slate">
                    {item.description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductDemo;
